import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  api,
  getStoredSession,
  isUsablePlatformSession,
  refreshPlatformSession,
  setStoredSession,
  type PlatformUser,
  type Session,
} from "./api";

type AuthState = {
  user: PlatformUser | null;
  loading: boolean;
  refresh: () => Promise<void>;
  signOut: () => void;
  setSession: (session: Session) => void;
};

const AuthCtx = createContext<AuthState | null>(null);

const SESSION_STORAGE_KEY = "tb_platform_session";

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<PlatformUser | null>(null);
  const [loading, setLoading] = useState(true);
  const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const userRef = useRef<PlatformUser | null>(null);
  userRef.current = user;

  const clearRefreshTimer = useCallback(() => {
    if (refreshTimer.current) {
      clearTimeout(refreshTimer.current);
      refreshTimer.current = null;
    }
  }, []);

  const scheduleProactiveRefresh = useCallback(() => {
    clearRefreshTimer();
    const stored = getStoredSession();
    if (!stored?.refresh_token || !stored.expires_at) return;
    // Refresh 60s before access token expires (min delay 5s).
    const delay = Math.max(stored.expires_at - Date.now() - 60_000, 5_000);
    refreshTimer.current = setTimeout(() => {
      void (async () => {
        const next = await refreshPlatformSession();
        if (next && isUsablePlatformSession(next)) {
          setUser(next.user);
          scheduleProactiveRefresh();
          return;
        }
        // Transient failure keeps stored session; only clear UI if storage wiped.
        if (!getStoredSession()?.refresh_token) {
          setUser(null);
        } else {
          scheduleProactiveRefresh();
        }
      })();
    }, delay);
  }, [clearRefreshTimer]);

  const refresh = useCallback(async () => {
    const stored = getStoredSession();

    // If access token is expired/near-expiry and we have a refresh token, renew first.
    if (
      stored?.refresh_token &&
      stored.expires_at &&
      Date.now() >= stored.expires_at - 5_000
    ) {
      const renewed = await refreshPlatformSession();
      if (!renewed) {
        // Definitive failure clears storage; transient keeps it (D-0073).
        if (!getStoredSession()?.refresh_token) {
          setUser(null);
          setLoading(false);
          return;
        }
        // Keep prior user from storage when possible; still try me() below.
        if (stored.user && !userRef.current) {
          setUser(stored.user);
        }
      } else if (renewed.user) {
        setUser(renewed.user);
      }
    } else if (stored?.user && !userRef.current) {
      // Avoid signed-out flash while me() is in flight.
      setUser(stored.user);
    }

    try {
      const { data } = await api.me();
      setUser(data);
      scheduleProactiveRefresh();
    } catch {
      // One more refresh attempt if me() failed due to expired access token.
      if (getStoredSession()?.refresh_token) {
        const renewed = await refreshPlatformSession();
        if (renewed) {
          try {
            const { data } = await api.me();
            setUser(data);
            scheduleProactiveRefresh();
            return;
          } catch {
            /* fall through */
          }
        }
        // Transient refresh/me failure: keep session if still stored.
        if (getStoredSession()?.refresh_token) {
          const kept = getStoredSession();
          if (kept?.user) setUser(kept.user);
          scheduleProactiveRefresh();
          return;
        }
      }
      setStoredSession(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [scheduleProactiveRefresh]);

  useEffect(() => {
    void refresh();
    return () => clearRefreshTimer();
  }, [refresh, clearRefreshTimer]);

  // Multi-tab storage sync (D-0073).
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key !== SESSION_STORAGE_KEY && event.key !== null) return;
      const stored = getStoredSession();
      if (!stored?.access_token) {
        clearRefreshTimer();
        setUser(null);
        return;
      }
      if (stored.user) {
        setUser(stored.user);
      }
      scheduleProactiveRefresh();
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [clearRefreshTimer, scheduleProactiveRefresh]);

  // Refresh when tab becomes visible and token is near expiry (D-0073).
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState !== "visible") return;
      const stored = getStoredSession();
      if (!stored?.refresh_token) return;
      if (
        stored.expires_at &&
        Date.now() >= stored.expires_at - 60_000
      ) {
        void refreshPlatformSession().then((next) => {
          if (next?.user) {
            setUser(next.user);
            scheduleProactiveRefresh();
          } else if (!getStoredSession()?.refresh_token) {
            setUser(null);
          }
        });
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [scheduleProactiveRefresh]);

  const value = useMemo<AuthState>(
    () => ({
      user,
      loading,
      refresh,
      signOut: () => {
        clearRefreshTimer();
        const stored = getStoredSession();
        const rt = stored?.refresh_token;
        setStoredSession(null);
        setUser(null);
        void api.portalSignout().catch(() => undefined);
        if (rt) {
          void api.signout(rt).catch(() => {
            /* best-effort revoke */
          });
        }
      },
      setSession: (session) => {
        if (!isUsablePlatformSession(session)) {
          return;
        }
        setStoredSession(session);
        setUser(session.user);
        scheduleProactiveRefresh();
      },
    }),
    [user, loading, refresh, clearRefreshTimer, scheduleProactiveRefresh],
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth outside provider");
  return ctx;
}
